import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash, ArrowsClockwise, SealCheck, CurrencyDollar, X, Bank, CalendarCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../../lib/api";
import { SectionHeader, StatusChip, Money, useFocusHighlight, ConfirmDialog } from "../../components/shared";
import { CsvTools } from "../../components/CsvTools";
import { DataTable } from "../../components/DataTable";
import { PURCHASE_EXTRA } from "../../lib/extraColumns";
import { CurrencyBadge, Field } from "../../components/DocumentCardParts";
import { ReceiveDialog } from "../../components/ReceiveDialog";
import { poCanReceive, poCanInvoice, poHasReceipts } from "../../lib/receiving";
import { Button } from "../../components/ui/button";
import { usePerms } from "../../lib/perms";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const PDOC_LABEL = { Quote: "Purchase Quote", Order: "Purchase Order", Invoice: "Purchase Invoice" };
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function PurchaseDocList({ docType, variant, statusFilter: sfProp, setStatusFilter: setSfProp, colSort, setColSort, colFilters, setColFilters }) {
  const qc = useQueryClient();
  const isActive = variant === "active";
  const { can } = usePerms();
  const canCreate = isActive && can("purchasing.edit");
  const canPost = can("purchasing.post");
  const canPay = can("purchasing.payments");
  const label = PDOC_LABEL[docType];
  const backTab = { Quote: "quotes", Order: "orders", Invoice: "invoices" }[docType] || "orders";
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null);
  const [delDoc, setDelDoc] = useState(null);
  const { rowProps } = useFocusHighlight();
  const showExt = true;
  const controls = setColSort ? { sort: colSort, setSort: setColSort, filters: colFilters, setFilters: setColFilters } : undefined;
  const [payOpen, setPayOpen] = useState(false);
  const [payDoc, setPayDoc] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const [sfLocal, setSfLocal] = useState("all");
  const statusFilter = sfProp ?? sfLocal;
  const setStatusFilter = setSfProp ?? setSfLocal;
  const [selected, setSelected] = useState([]);
  const [runOpen, setRunOpen] = useState(false);
  const [runAmount, setRunAmount] = useState("");
  const [runMethod, setRunMethod] = useState("Bank Transfer");
  const [runResult, setRunResult] = useState(null);

  const [schedOpen, setSchedOpen] = useState(false);
  const [sched, setSched] = useState({ enabled: false, frequency: "monthly", weekday: 1, day_of_month: 1, amount: "", method: "Bank Transfer", email_summary: true });
  const schedQuery = useQuery({ queryKey: ["payment-run-schedule"], queryFn: async () => (await api.get("/payment-run-schedule")).data, enabled: schedOpen });
  useEffect(() => { if (schedQuery.data) setSched({ ...schedQuery.data, amount: schedQuery.data.amount || "" }); }, [schedQuery.data]);
  const saveSched = useMutation({
    mutationFn: async () => (await api.put("/payment-run-schedule", { ...sched, amount: Number(sched.amount || 0), weekday: Number(sched.weekday), day_of_month: Number(sched.day_of_month) })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payment-run-schedule"] }); setSchedOpen(false); toast.success(sched.enabled ? "Payment run schedule saved" : "Schedule disabled"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const { data: pos = [] } = useQuery({ queryKey: ["purchase-orders"], queryFn: async () => (await api.get("/purchase-orders")).data });

  const today = new Date().toISOString().slice(0, 10);
  const docs = pos.filter((o) => {
    const dt = o.doc_type || "Order";
    if (dt !== docType) return false;
    if (variant === "active") return !o.archived && !o.posted && o.status !== "Received";
    if (variant === "archive") return o.archived;
    if (variant === "posted") return o.posted;
    return false;
  });

  const [docSp, setDocSp] = useSearchParams();
  const soFilter = docSp.get("so") || "";
  const clearSoFilter = () => { const p = new URLSearchParams(docSp); p.delete("so"); setDocSp(p, { replace: true }); };
  const setSoFilter = (v) => { const p = new URLSearchParams(docSp); if (v) p.set("so", v); else p.delete("so"); setDocSp(p, { replace: true }); };
  const soOptions = [...new Set(pos.map((o) => o.sales_order_no).filter(Boolean))].sort();

  const filteredDocs = docs.filter((o) => {
    if (soFilter && o.sales_order_no !== soFilter) return false;
    if (statusFilter === "all") return true;
    if (statusFilter === "Overdue") return ["Open", "Partial"].includes(o.status) && (o.due_date || o.expected_date) && (o.due_date || o.expected_date) < today;
    return o.status === statusFilter;
  });
  const isInvoiceList = docType === "Invoice" && isActive;
  const selectableIds = isInvoiceList ? filteredDocs.filter((o) => o.status !== "Paid").map((o) => o.id) : [];
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const outstandingTotal = round2(filteredDocs.filter((o) => o.status !== "Paid").reduce((s, o) => s + (o.total - (o.amount_paid || 0)), 0));
  const displayDocs = filteredDocs;

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    qc.invalidateQueries({ queryKey: ["vendors"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["items"] });
    qc.invalidateQueries({ queryKey: ["posted-receipts"] });
  };

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/purchase-orders/${id}`),
    onSuccess: () => { invalidateAll(); toast.success("Deleted"); },
  });
  const convert = useMutation({
    mutationFn: async (id) => (await api.post(`/purchase-orders/${id}/convert-to-order`)).data,
    onSuccess: (res) => { invalidateAll(); toast.success(`Converted to order ${res.doc_no}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const [receiveDoc, setReceiveDoc] = useState(null);
  const makeInvoice = useMutation({
    mutationFn: async ({ id, ext }) => (await api.post(`/purchase-orders/${id}/convert-to-invoice`, { external_doc_no: ext.trim() })).data,
    onSuccess: (res) => { invalidateAll(); toast.success(`Purchase invoice ${res.doc_no} created`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const vendorPayment = useMutation({
    mutationFn: async () => (await api.post(`/purchase-orders/${payDoc.id}/payments`, { amount: Number(payAmount), method: payMethod })).data,
    onSuccess: (res) => { invalidateAll(); setPayOpen(false); toast.success(res.remaining > 0 ? `Paid — ${money(res.remaining)} remaining` : "Invoice fully paid"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const openPayment = (o) => { setPayDoc(o); setPayAmount(String(round2(o.total - (o.amount_paid || 0)))); setPayMethod("Bank Transfer"); setPayOpen(true); };
  const paymentRun = useMutation({
    mutationFn: async () => (await api.post("/purchase-orders/payment-run", {
      amount: Number(runAmount), method: runMethod, invoice_ids: selected,
    })).data,
    onSuccess: (res) => { invalidateAll(); setRunResult(res); setSelected([]); toast.success(`Payment run: ${money(res.total_paid)} across ${res.count} invoice${res.count === 1 ? "" : "s"}`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const openRun = () => { setRunAmount(String(selected.length ? round2(filteredDocs.filter((o) => selected.includes(o.id)).reduce((s, o) => s + (o.total - (o.amount_paid || 0)), 0)) : outstandingTotal)); setRunMethod("Bank Transfer"); setRunResult(null); setRunOpen(true); };
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (isInvoiceList && sp.get("action") === "payment-run") {
      openRun();
      const p = new URLSearchParams(sp); p.delete("action"); setSp(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, isInvoiceList]);

  const titles = {
    active: { Quote: ["Purchase Quotes", "Requests for quotation"], Order: ["Purchase Orders", "Procure to pay"], Invoice: ["Purchase Invoices", "Vendor bills"] },
    archive: { Quote: ["Purchase Quote Archives", "Converted quotes"] },
  };
  const [title, subtitle] = (titles[variant] && titles[variant][docType]) || [label, ""];

  return (
    <>
      <SectionHeader title={title} subtitle={subtitle}>
        <div className="flex items-center gap-2 flex-wrap">
          <CsvTools
            entity={`purchase-${docType.toLowerCase()}`}
            exportOnly
            label="documents"
            rows={displayDocs}
            columns={[
              { key: "doc_no", label: "doc_no" },
              { key: "doc_type", label: "type" },
              { key: "vendor_name", label: "vendor" },
              { key: "external_doc_no", label: "external_doc_no" },
              { key: "sales_order_no", label: "sales_order_no" },
              { key: "date", label: "date" },
              { key: "due_date", label: "due_date" },
              { key: "status", label: "status" },
              { key: "subtotal", label: "subtotal" },
              { key: "tax", label: "tax" },
              { key: "total", label: "total" },
              { key: "amount_paid", label: "amount_paid" },
            ]}
          />
          {canCreate && (
            <Button onClick={() => navigate(`/purchasing/doc/new?type=${docType}&tab=${backTab}`)} data-testid={`new-purchase-${docType.toLowerCase()}-button`} className="rounded-sm bg-primary hover:bg-primary/90 h-9">
              <Plus size={16} weight="bold" className="mr-1" /> {`New ${label}`}
            </Button>
          )}
        </div>
      </SectionHeader>

      {isActive && (
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-1.5" data-testid="purchase-status-filters">
            {["all", "Open", "Partial", "Paid", "Overdue"].map((s) => (
              <button
                key={s} onClick={() => setStatusFilter(s)} data-testid={`purchase-filter-${s.toLowerCase()}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                  statusFilter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"
                }`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {soOptions.length > 0 && (
              <div className="flex items-center gap-1.5">
                <Select value={soFilter || "all"} onValueChange={(v) => setSoFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="rounded-sm h-8 text-xs w-52" data-testid="po-so-filter-select"><SelectValue placeholder="Filter by Sales Order" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sales Orders</SelectItem>
                    {soOptions.map((so) => <SelectItem key={so} value={so}>{so}</SelectItem>)}
                  </SelectContent>
                </Select>
                {soFilter && (
                  <button onClick={clearSoFilter} data-testid="po-so-filter-clear" className="text-muted-foreground hover:text-destructive p-1" title="Clear sales order filter"><X size={14} /></button>
                )}
              </div>
            )}
            {isInvoiceList && canPay && (
              <Button variant="outline" onClick={() => setSchedOpen(true)} data-testid="schedule-run-button" className="rounded-sm h-8 text-xs">
                <CalendarCheck size={14} weight="bold" className="mr-1" /> Schedule
              </Button>
            )}
            {isInvoiceList && canPay && selectableIds.length > 0 && (
              <Button onClick={openRun} data-testid="payment-run-button" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
                <Bank size={14} weight="bold" className="mr-1" /> Payment Run{selected.length > 0 ? ` (${selected.length})` : ""}
              </Button>
            )}
          </div>
        </div>
      )}

      <DataTable
        testid="purchase-docs-table"
        data={displayDocs}
        controls={controls}
        rowKey={(p) => p.id}
        rowTestid="po-row"
        viewKey={`purchase-${docType}`} extraColumns={PURCHASE_EXTRA}
        rowProps={(p) => rowProps(p.id)}
        empty="Nothing here yet."
        leading={isInvoiceList && canPay ? {
          header: () => <input type="checkbox" data-testid="select-all-pinvoices" checked={selectableIds.length > 0 && selected.length === selectableIds.length} onChange={(e) => setSelected(e.target.checked ? selectableIds : [])} className="accent-primary" />,
          cell: (p) => (p.status !== "Paid" ? <input type="checkbox" data-testid="select-pinvoice" checked={selected.includes(p.id)} onChange={() => toggleSel(p.id)} className="accent-primary" /> : null),
        } : undefined}
        columns={[
          { key: "doc_no", header: "Doc No.", filter: "text", cellClassName: "font-mono text-xs font-medium", cell: (p) => (
            <>
              <button onClick={() => navigate(`/purchasing/doc/${p.id}?tab=${backTab}`)} data-testid="open-po-doc" className="text-primary hover:underline font-medium">{p.doc_no}</button>
              {p.source_doc_no && <span className="text-muted-foreground ml-1">← {p.source_doc_no}</span>}
              {p.sales_order_no && <div className="text-xs text-muted-foreground font-sans" data-testid="po-sales-order-no">SO: {p.sales_order_no}</div>}
              {p.drop_ship && <span className="inline-block mt-0.5 text-[10px] uppercase font-bold text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded-sm" data-testid="po-drop-ship-badge">Drop-ship → {p.ship_to_name}</span>}
            </>
          ) },
          { key: "vendor_name", header: "Vendor", filter: "text", cellClassName: "text-sm font-medium" },
          { key: "created_by_name", header: "Raised By", filter: "text", sortable: true, cellClassName: "text-sm text-muted-foreground", cell: (p) => {
            const who = p.created_by_name || p.purchaser || p.approval_submitted_by;
            if (!who) return "—";
            return <span data-testid="po-raised-by"><span className="text-foreground">{who}</span>{p.created_by_role ? <span className="block text-xs capitalize">{p.created_by_role}</span> : null}</span>;
          } },
          ...(showExt ? [{ key: "external_doc_no", header: "External Doc No.", filter: "text", cellClassName: "text-sm text-muted-foreground", cell: (p) => <span data-testid="po-ext-doc-no-cell">{p.external_doc_no || "—"}</span> }] : []),
          { key: "date", header: "Date", filter: "text", cellClassName: "text-sm text-muted-foreground font-mono" },
          { key: "status", header: "Status", filter: false, cell: (p) => <span className="inline-flex items-center gap-1 flex-wrap"><StatusChip status={p.status} />{p.receipt_status === "Partially Received" && <span className="px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm border-amber-200 bg-amber-50 text-amber-700" data-testid="po-partial-received-badge">Part received</span>}{!p.archived && Number(p.invoiced_amount || 0) > 0 && <span className="px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm border-violet-200 bg-violet-50 text-violet-700" data-testid="po-partial-invoiced-badge">Part invoiced</span>}</span> },
          { key: "total", header: "Total", align: "right", numeric: true, cell: (p) => (
            <div className="flex items-center justify-end gap-1.5">
              <CurrencyBadge currency={p.currency} />
              <div><Money value={p.total} className="text-sm font-medium" />{p.amount_paid > 0 && p.status !== "Paid" && (<div className="text-xs text-amber-600 tabular">{money(p.total - p.amount_paid)} due</div>)}</div>
            </div>
          ) },
          { key: "actions", header: "Actions", align: "right", sortable: false, cell: (p) => (
            <div className="flex justify-end gap-1">
              {isActive && canPost && docType === "Quote" && (<button onClick={() => setConfirm({ kind: "order", id: p.id, doc_no: p.doc_no })} data-testid="convert-pquote" title="Convert to order" className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"><ArrowsClockwise size={16} weight="bold" /></button>)}
              {isActive && canPost && docType === "Order" && (<>{poCanInvoice(p) && <button onClick={() => setConfirm({ kind: "invoice", id: p.id, doc_no: p.doc_no, ext: p.external_doc_no || "" })} data-testid="convert-po-invoice" title="Invoice received quantities" className="p-1.5 text-muted-foreground hover:text-violet-600 transition-colors duration-150"><ArrowsClockwise size={16} weight="bold" /></button>}{poCanReceive(p) && <button onClick={() => setReceiveDoc(p)} data-testid="post-receipt" title="Receive into stock" className="p-1.5 text-muted-foreground hover:text-emerald-600 transition-colors duration-150"><SealCheck size={16} weight="bold" /></button>}</>)}
              {isActive && canPay && docType === "Invoice" && p.status !== "Paid" && (<button onClick={() => openPayment(p)} data-testid="vendor-payment" title="Record payment" className="p-1.5 text-muted-foreground hover:text-emerald-600 transition-colors duration-150"><CurrencyDollar size={16} weight="bold" /></button>)}
              {isActive && can("purchasing.delete") && !poHasReceipts(p) && !(Number(p.invoiced_amount || 0) > 0) && (<button onClick={() => setDelDoc(p)} data-testid="delete-po" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors duration-150"><Trash size={16} /></button>)}
              {!isActive && <span className="text-xs text-muted-foreground pr-1">—</span>}
            </div>
          ) },
        ]}
      />

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CurrencyDollar size={20} className="text-primary" /> Pay Vendor Invoice</DialogTitle></DialogHeader>
          {payDoc && (
            <div className="space-y-4 py-2">
              <div className="border border-border bg-secondary/50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono font-medium">{payDoc.doc_no}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="tabular">{money(payDoc.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Already paid</span><span className="tabular">{money(payDoc.amount_paid || 0)}</span></div>
                <div className="flex justify-between font-bold"><span>Remaining</span><span className="tabular text-primary">{money(payDoc.total - (payDoc.amount_paid || 0))}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline-label">Amount</Label><Input type="number" step="0.01" data-testid="vendor-payment-amount" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="mt-1 rounded-sm num" /></div>
                <div>
                  <Label className="overline-label">Method</Label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="vendor-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => vendorPayment.mutate()} disabled={!payAmount || Number(payAmount) <= 0 || vendorPayment.isPending} data-testid="save-vendor-payment" className="rounded-sm bg-primary hover:bg-primary/90">{vendorPayment.isPending ? "Saving…" : "Record Payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={runOpen} onOpenChange={setRunOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Bank size={20} className="text-primary" /> Payment Run</DialogTitle></DialogHeader>
          {!runResult ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Enter a total budget. It will be allocated to {selected.length > 0 ? `the ${selected.length} selected invoice${selected.length === 1 ? "" : "s"}` : "all open vendor invoices"} <strong>oldest due date first</strong> until the budget runs out.
              </p>
              <div className="border border-border bg-secondary/50 p-3 text-sm flex justify-between">
                <span className="text-muted-foreground">Outstanding in scope</span>
                <span className="tabular font-bold text-primary">{money(selected.length ? filteredDocs.filter((o) => selected.includes(o.id)).reduce((s, o) => s + (o.total - (o.amount_paid || 0)), 0) : outstandingTotal)}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="overline-label">Total Budget</Label><Input type="number" step="0.01" data-testid="run-amount-input" value={runAmount} onChange={(e) => setRunAmount(e.target.value)} className="mt-1 rounded-sm num" /></div>
                <div>
                  <Label className="overline-label">Method</Label>
                  <Select value={runMethod} onValueChange={setRunMethod}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="run-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2" data-testid="run-result">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border border-border bg-secondary/50 p-3"><p className="overline-label">Total Paid</p><p className="tabular font-bold text-emerald-600 text-lg">{money(runResult.total_paid)}</p></div>
                <div className="border border-border bg-secondary/50 p-3"><p className="overline-label">Budget Left</p><p className="tabular font-bold text-lg">{money(runResult.leftover)}</p></div>
              </div>
              <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto">
                {runResult.allocations.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No invoices were paid.</div>}
                {runResult.allocations.map((a, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid="run-allocation">
                    <div className="min-w-0"><span className="font-mono text-xs font-medium">{a.doc_no}</span> <span className="text-muted-foreground truncate">{a.vendor_name}</span></div>
                    <div className="text-right shrink-0">
                      <span className="tabular font-semibold text-emerald-600">{money(a.applied)}</span>
                      <StatusChip status={a.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            {!runResult ? (
              <>
                <Button variant="outline" onClick={() => setRunOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={() => paymentRun.mutate()} disabled={!runAmount || Number(runAmount) <= 0 || paymentRun.isPending} data-testid="execute-run-button" className="rounded-sm bg-emerald-600 hover:bg-emerald-700">{paymentRun.isPending ? "Processing…" : "Run Payment"}</Button>
              </>
            ) : (
              <Button onClick={() => setRunOpen(false)} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="close-run-button">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="schedule-run-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarCheck size={20} className="text-primary" /> Recurring Payment Run</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="sched-enabled" checked={sched.enabled} onChange={(e) => setSched((s) => ({ ...s, enabled: e.target.checked }))} className="accent-primary h-4 w-4" />
              <span className="font-medium">Enable recurring payment run</span>
            </label>
            <p className="text-xs text-muted-foreground -mt-2">On each run, the budget is paid across open vendor invoices, oldest due date first.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline-label">Frequency</Label>
                <Select value={sched.frequency} onValueChange={(v) => setSched((s) => ({ ...s, frequency: v }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="sched-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {sched.frequency === "weekly" ? (
                <div>
                  <Label className="overline-label">Day of week</Label>
                  <Select value={String(sched.weekday)} onValueChange={(v) => setSched((s) => ({ ...s, weekday: Number(v) }))}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="sched-weekday"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="overline-label">Day of month</Label>
                  <Input type="number" min="1" max="31" data-testid="sched-day-of-month" value={sched.day_of_month} onChange={(e) => setSched((s) => ({ ...s, day_of_month: e.target.value }))} className="mt-1 rounded-sm num" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="overline-label">Budget per run</Label><Input type="number" step="0.01" data-testid="sched-amount" value={sched.amount} onChange={(e) => setSched((s) => ({ ...s, amount: e.target.value }))} className="mt-1 rounded-sm num" placeholder="10000" /></div>
              <div>
                <Label className="overline-label">Method</Label>
                <Select value={sched.method} onValueChange={(v) => setSched((s) => ({ ...s, method: v }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="sched-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Check">Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="sched-email-summary" checked={sched.email_summary} onChange={(e) => setSched((s) => ({ ...s, email_summary: e.target.checked }))} className="accent-primary h-4 w-4" />
              <span>Email a run summary after each automatic run</span>
            </label>
            {sched.last_run_date && (
              <p className="text-xs text-muted-foreground border-t border-border pt-3" data-testid="sched-last-run">Last automatic run: {sched.last_run_date} — paid {money(sched.last_run_total || 0)} across {sched.last_run_count || 0} invoice(s).</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => saveSched.mutate()} disabled={saveSched.isPending || (sched.enabled && !(Number(sched.amount) > 0))} data-testid="save-schedule-button" className="rounded-sm bg-primary hover:bg-primary/90">{saveSched.isPending ? "Saving…" : "Save Schedule"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReceiveDialog open={!!receiveDoc} onOpenChange={(v) => { if (!v) setReceiveDoc(null); }} po={receiveDoc} onDone={invalidateAll} />
      <ConfirmDialog
        open={!!confirm}
        confirmDisabled={confirm?.kind === "invoice" && !confirm.ext?.trim()}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title={confirm?.kind === "order" ? "Convert quote to order?" : "Create purchase invoice?"}
        description={confirm ? (confirm.kind === "invoice" ? `A purchase invoice will be created for everything received on ${confirm.doc_no} that hasn't been invoiced yet.` : `${confirm.doc_no} will be converted. This cannot be undone.`) : ""}
        confirmLabel={confirm?.kind === "order" ? "Convert to Order" : "Create Invoice"}
        testid="po-convert-confirm-dialog"
        onConfirm={() => {
          if (confirm?.kind === "order") convert.mutate(confirm.id);
          else if (confirm?.kind === "invoice") makeInvoice.mutate({ id: confirm.id, ext: confirm.ext });
          setConfirm(null);
        }}
      >
        {confirm?.kind === "invoice" && (
          <Field label="Vendor Invoice No. (External Document No.)" required highlight={!confirm.ext?.trim()}>
            <Input value={confirm.ext} onChange={(e) => setConfirm((c) => ({ ...c, ext: e.target.value }))} className="rounded-sm" placeholder="e.g. INV-12345" data-testid="po-list-invoice-ext-doc" autoFocus />
          </Field>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!delDoc}
        onOpenChange={(v) => { if (!v) setDelDoc(null); }}
        title="Delete this document?"
        description={delDoc ? `${delDoc.doc_no} will be permanently deleted. This cannot be undone.` : ""}
        confirmLabel="Delete"
        testid="po-delete-confirm-dialog"
        onConfirm={() => { if (delDoc) del.mutate(delDoc.id); setDelDoc(null); }}
      />
    </>
  );
}
